import axios, { AxiosError, InternalAxiosRequestConfig } from "axios";
import createAuthRefreshInterceptor from "axios-auth-refresh";

import { getToken, storeToken, clearSession } from "./session";
import { resetUser } from "./posthog";
import { getSlug } from "../routes";

const basePath = {
  baseURL: process.env.REACT_APP_API,
};

const instance = axios.create();
const interceptedInstance = axios.create();
// without pauseInstanceWhileRefreshing, parallel requests on mount (e.g. dashboard) could each race their own logout on a 401
createAuthRefreshInterceptor(interceptedInstance, refreshTokenInterceptor, {
  pauseInstanceWhileRefreshing: true,
  shouldRefresh: (error: AxiosError) =>
    error.response?.status === 401 && !isUnconfirmedUserResponse(error),
});

// /users/me answers 401 with the user (confirmed: false) until the email is
// confirmed. The token is valid, so refreshing would only burn the refresh
// token and, for sessions without one, log the user out on every page load.
function isUnconfirmedUserResponse(error: AxiosError) {
  return (error.response?.data as any)?.confirmed === false;
}

interceptedInstance.interceptors.request.use(requestInterceptor);

function requestInterceptor(request: InternalAxiosRequestConfig) {
  // console.log(`[requestInterceptor]`)
  // get token
  const token = getToken();

  // if no token, stop, redirect to login
  if (!token) return goToLogin();

  request.headers.set("Authorization", `Bearer ${token.access_token}`);
  // continue with the request
  return request;
}

async function requestAccessToken(refresh_token: string) {
  const url = `${basePath.baseURL}/oauth/v2/token`;
  const params = {
    client_id: "1_qwgh3gijy6oc44kw04wco0swookgwgws0csww4cgw4oss80s4",
    client_secret: "44eugnuavykgosskw00c84wwoosc4oosk80000w88o48wkssso",
    grant_type: "refresh_token",
    refresh_token,
  };

  try {
    const token = await instance.post(url, params);
    return storeToken(token);
  } catch (error) {
    throw error;
  }
}

// axios-auth-refresh v5 passes the failed request's AxiosError, not its config
async function refreshTokenInterceptor(failedRequest: AxiosError) {
  // get token
  const currentToken = getToken();
  let token;
  try {
    // requestAccessToken already stores the new token
    token = (await requestAccessToken(currentToken.refresh_token)).data;
  } catch (error) {
    console.error(`[requestInterceptor]`, error);
    clearSession();
    resetUser();

    return goToLogin();
  }
  failedRequest.response?.config.headers.set(
    "Authorization",
    `Bearer ${token.access_token}`
  );
}

function goToLogin() {
  const slug = getSlug();

  window.location.href = `/${slug}/login?redirect=${window.location.pathname}`;
  return Promise.reject({ error: "No token!" });
}

export { interceptedInstance as api, instance as openApi, basePath };
